import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { GlowingBackgroundContainer } from "@/components/effects";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Icon } from "@/components/ui/Icon";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/ui/Logo";
import { ROUTES } from "@/constants/routes";
import { AUTH_QUERY_KEYS } from "@/hooks/useAuth";
import { useCancelTonUsdtInvoice, useTonUsdtInvoiceStatus } from "@/hooks/useTonUsdtInvoiceStatus";
import { useTonUsdtPayment } from "@/hooks/useTonUsdtPayment";
import { TON_USDT_MIN_AMOUNT, toManualTonUsdtPayment } from "@/lib/manualTonUsdtPayment";
import { formatCurrency } from "@/lib/utils";
import { useUserStore } from "@/store/userStore";

import type { TonUsdtInvoice } from "@/types/tonUsdtPayment";

interface CopyableValueProps {
  label: string;
  value: string;
}

const getPaymentErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Не удалось подготовить реквизиты. Попробуйте ещё раз.";

const CopyableValue = ({ label, value }: CopyableValueProps) => {
  const copyValue = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} скопирован`);
    } catch {
      toast.error("Не удалось скопировать. Скопируйте значение вручную.");
    }
  };

  return (
    <div className="rounded-sm bg-neutral-950/45 px-3 py-2">
      <p className="fs-200 mb-1 font-semibold tracking-wide text-neutral-200/50 uppercase">
        {label}
      </p>
      <div className="flex items-center gap-2">
        <span className="fs-300 min-w-0 flex-1 font-mono font-bold break-all text-neutral-50">
          {value}
        </span>
        <button
          type="button"
          onClick={copyValue}
          className="focus-visible:ring-primary-500 flex size-8 shrink-0 items-center justify-center rounded-sm bg-neutral-50/5 text-neutral-50/60 transition-colors hover:bg-neutral-50/10 hover:text-neutral-50 focus-visible:ring-2 focus-visible:outline-none"
          aria-label={`Копировать: ${label}`}
        >
          <Icon
            name="copy"
            className="size-4"
          />
        </button>
      </div>
    </div>
  );
};

const formatRemainingTime = (remainingSeconds: number) => {
  const hours = Math.floor(remainingSeconds / 3600);
  const minutes = Math.floor((remainingSeconds % 3600) / 60);
  const seconds = remainingSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const PaymentStatus = ({
  invoice,
  remainingSeconds,
}: {
  invoice: TonUsdtInvoice;
  remainingSeconds: number | null;
}) => {
  const isConfirmed = invoice.status === "confirmed";
  const needsManualReview = invoice.status === "manual_review" || invoice.status === "rejected";
  const hasExpired = invoice.status === "expired" || remainingSeconds === 0;
  const isCancelled = invoice.status === "cancelled";
  const isPending = invoice.status === "pending";
  const title = isConfirmed
    ? "Платёж подтверждён"
    : needsManualReview
      ? "Платёж требует проверки"
      : hasExpired
        ? "Срок реквизитов истёк"
        : isCancelled
          ? "Реквизиты отменены"
          : isPending
            ? "Ожидаем перевод"
            : "Ожидаем подтверждения сети TON";
  const description = isConfirmed
    ? "Топливо будет обновлено автоматически."
    : needsManualReview
      ? "Перевод не был зачислен автоматически. Обратитесь в поддержку, указав код платежа."
      : hasExpired
        ? "Эти реквизиты больше не принимаются. Вернитесь к покупке топлива, чтобы создать новый платёж."
        : isCancelled
          ? "Это не отменит уже отправленный перевод в сети TON. Если уже отправили USDT, обратитесь в поддержку."
          : isPending
            ? `Действуют ещё ${formatRemainingTime(remainingSeconds ?? 0)}. Переведите ровно указанную сумму USDT с кодом платежа из реквизитов выше.`
            : "Перевод найден. Обычно подтверждение сети занимает несколько минут.";
  const markerClassName = needsManualReview
    ? "bg-danger"
    : hasExpired
      ? "bg-neutral-500"
      : isCancelled
        ? "bg-neutral-500"
        : "bg-primary-500";

  return (
    <p
      role="status"
      className="fs-200 mb-6 ml-2 flex items-start gap-2 leading-relaxed text-neutral-200/70"
    >
      <span className={`mt-2 size-1.5 shrink-0 rounded-full ${markerClassName}`} />
      <span>
        <span className="font-semibold text-neutral-200">{title}.</span> {description}
      </span>
    </p>
  );
};

export function FuelPurchasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { user } = useUserStore();
  const fuelBalance = Number(user?.fuel ?? "0");

  const [amount, setAmount] = useState<number>(500);
  const [invoice, setInvoice] = useState<TonUsdtInvoice | null>(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const { createInvoice, isSubmitting } = useTonUsdtPayment();
  const { cancelInvoice, isCancellingInvoice } = useCancelTonUsdtInvoice();
  const invoiceId = invoice?.id ?? searchParams.get("invoice");
  const { data: invoiceStatus } = useTonUsdtInvoiceStatus(invoiceId);
  const confirmedInvoiceId = useRef<string | null>(null);
  const displayedInvoice = invoiceStatus ?? invoice;
  const [now, setNow] = useState(() => Date.now());
  const remainingSeconds =
    displayedInvoice?.status === "pending"
      ? Math.max(0, Math.ceil((Date.parse(displayedInvoice.expires_at) - now) / 1000))
      : null;
  const payment =
    displayedInvoice && displayedInvoice.status !== "cancelled" && remainingSeconds !== 0
      ? toManualTonUsdtPayment(displayedInvoice)
      : null;

  const presetAmounts = [100, 500, 1000, 2000, 5000];

  const handlePresetClick = (value: number) => setAmount(value);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "") {
      setAmount(0);
      return;
    }
    const num = Number(val);
    if (Number.isFinite(num) && num >= 0) setAmount(num);
  };

  useEffect(() => {
    if (
      displayedInvoice?.status !== "confirmed" ||
      confirmedInvoiceId.current === displayedInvoice.id
    ) {
      return;
    }

    confirmedInvoiceId.current = displayedInvoice.id;
    toast.success("Платёж подтверждён сетью TON. Обновляем топливо.");
    void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.USER });
  }, [displayedInvoice, queryClient]);

  useEffect(() => {
    if (displayedInvoice?.status !== "pending" || remainingSeconds === 0) return;

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [displayedInvoice?.status, remainingSeconds]);

  const handleCreateInvoice = async () => {
    try {
      const createdInvoice = await createInvoice(amount.toFixed(2));
      setInvoice(createdInvoice);
      toast.success("Реквизиты для перевода готовы.");
    } catch (error) {
      toast.error(getPaymentErrorMessage(error));
    }
  };

  const handleCancelInvoice = async () => {
    if (!displayedInvoice) return;

    try {
      await cancelInvoice(displayedInvoice.id);
      toast.success(
        "Реквизиты отменены. Новый перевод по этому коду не будет зачислен автоматически."
      );
      setIsCancelDialogOpen(false);
    } catch {
      toast.error("Не удалось отменить реквизиты. Обновите страницу и попробуйте ещё раз.");
    }
  };

  return (
    <div className="flex h-dvh w-full flex-col gap-4 overflow-hidden bg-neutral-950 p-4 sm:p-5 md:flex-row">
      {/* Правая часть (СЦЕНА) */}
      <div
        // prettier-ignore
        className="
          order-1 min-w-0
          h-[clamp(145px,20vh,185px)]
          md:order-2 md:flex-1 md:h-full md:self-stretch
        "
      >
        <GlowingBackgroundContainer>
          <div className="relative size-full overflow-hidden">
            <img
              src="/images/buyFuel/lightning.png"
              alt=""
              // prettier-ignore
              className="
                pointer-events-none select-none
                /* DESKTOP */
                md:absolute md:top-[54%] md:left-[52%] md:-translate-x-1/2 md:-translate-y-1/2
                md:h-full md:w-auto md:object-contain md:min-w-[156%]

                /* MOBILE */
                absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-[12%]
                h-[min(100%,_30vh)] w-auto object-contain min-w-[145%]
              "
            />
          </div>
        </GlowingBackgroundContainer>
      </div>

      {/* Левая часть */}
      <div
        // prettier-ignore
        className="
          order-2 flex w-full flex-1 min-h-0 flex-col bg-neutral-950
          rounded-mds sm:border-2 sm:border-neutral-900 sm:pt-6
          md:order-1 md:flex-1 md:p-4
        "
      >
        <div className="flex min-h-0 flex-1 flex-col md:justify-center">
          <div className="mx-auto flex min-h-0 w-full max-w-140 flex-1 flex-col md:flex-none">
            {/* scroll area */}
            <div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto md:flex-none md:overflow-y-visible">
              {/* ФОРМА */}
              <Logo />

              <div className="mt-4 mb-12 flex items-center justify-between gap-4">
                <h1 className="text-2xl font-bold text-neutral-100 lg:text-3xl">Покупка топлива</h1>
                <Link
                  to={ROUTES.topUps}
                  className="fs-200 text-primary-500 hover:text-primary-400 font-semibold transition-colors"
                >
                  Пополнения
                </Link>
              </div>

              {displayedInvoice ? (
                <>
                  {payment && (
                    <>
                      <div className="mb-6">
                        <p className="fs-300 mb-2 font-semibold text-neutral-200/50">
                          Пополнение топлива
                        </p>
                        <div className="fs-500 flex items-center gap-2 font-bold text-neutral-50">
                          {payment.amountUsdt}
                          <Icon
                            name="usdt"
                            className="h-4.5 w-4.5 text-neutral-50"
                          />
                        </div>
                      </div>

                      <div className="mb-6 space-y-3 rounded-sm border border-neutral-50/8 bg-neutral-50/2 p-4">
                        <p className="fs-300 font-bold text-neutral-50">
                          Переведите USDT в своём кошельке
                        </p>
                        <p className="fs-200 leading-relaxed text-neutral-200/70">
                          Выберите сеть TON, укажите сумму и вставьте код платежа в поле memo или
                          комментарий.
                        </p>
                        <CopyableValue
                          label="Сеть"
                          value={payment.network}
                        />
                        <CopyableValue
                          label="Сумма"
                          value={`${payment.amountUsdt} ${payment.asset}`}
                        />
                        <CopyableValue
                          label="Адрес получателя"
                          value={payment.receivingAddress}
                        />
                        <CopyableValue
                          label="Memo / комментарий"
                          value={payment.memo}
                        />
                        <p className="fs-200 leading-relaxed text-neutral-200/50">
                          Комиссию сети ваш кошелёк рассчитает отдельно. Отправьте ровно указанную
                          сумму и не меняйте memo.
                        </p>
                      </div>
                    </>
                  )}

                  <PaymentStatus
                    invoice={displayedInvoice}
                    remainingSeconds={remainingSeconds}
                  />
                </>
              ) : (
                <>
                  <p className="fs-300 mb-4 font-semibold text-neutral-200/50">Сколько купить?</p>

                  <div className="mb-5">
                    <div className="relative">
                      <Input
                        type="number"
                        min={TON_USDT_MIN_AMOUNT}
                        step="0.01"
                        value={amount}
                        onChange={handleInputChange}
                        className="fs-500 appearance-none rounded bg-neutral-50/5 py-3 pr-4 pl-10 text-left font-bold text-neutral-200 [-moz-appearance:textfield] focus-visible:ring-2 focus-visible:ring-[#c6fd00]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black focus-visible:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                      />
                      <div className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2">
                        <Icon
                          name="fuel"
                          className="h-5 w-5 text-neutral-400"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-12 flex flex-wrap gap-2">
                    {presetAmounts.map((val) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => handlePresetClick(val)}
                        className={`fs-300 rounded-full px-3 py-1.5 font-semibold transition-all ${
                          amount === val
                            ? "bg-white text-neutral-900"
                            : "bg-neutral-50/5 text-neutral-100 hover:bg-neutral-200/12"
                        }`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>

                  <p className="fs-200 -mt-8 mb-8 font-medium text-neutral-200/50">
                    Минимум {TON_USDT_MIN_AMOUNT} USDT. Курс: 1 USDT = 1 топливо.
                  </p>

                  <div className="mb-8 flex items-center gap-2">
                    <span className="fs-300 font-semibold text-neutral-200/50">Запас сейчас</span>
                    <div className="fs-400 flex items-center gap-1 font-bold text-neutral-50">
                      {formatCurrency(fuelBalance)}
                      <Icon
                        name="fuel"
                        className="h-4 w-4 text-neutral-50"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* чтобы на мобилке контент не прилипал к кнопкам */}
              <div className="h-6 md:hidden" />
            </div>

            {/* footer с кнопками: на мобилке он закреплён снизу,
                а на десктопе просто идёт после контента */}
            <div className="shrink-0 pt-4 pb-5 md:pb-0">
              <div className="flex flex-col gap-2">
                {displayedInvoice ? (
                  <>
                    <Button
                      variant="secondary"
                      size="lg"
                      className="w-full"
                      onClick={() => navigate(ROUTES.dashboard)}
                    >
                      К балансу
                    </Button>
                    {displayedInvoice.status === "pending" && remainingSeconds !== 0 && (
                      <Button
                        variant="destructive"
                        size="lg"
                        className="w-full"
                        onClick={() => setIsCancelDialogOpen(true)}
                      >
                        Отменить реквизиты
                      </Button>
                    )}
                  </>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="neutral"
                      size="lg"
                      className="flex-1"
                      onClick={() => navigate(-1)}
                    >
                      Отмена
                    </Button>

                    <Button
                      variant="secondary"
                      size="lg"
                      className="flex-1"
                      disabled={amount < Number(TON_USDT_MIN_AMOUNT) || isSubmitting}
                      onClick={handleCreateInvoice}
                    >
                      {isSubmitting ? "Подготавливаем…" : "Получить реквизиты"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={isCancelDialogOpen}
        onOpenChange={setIsCancelDialogOpen}
        title="Отменить реквизиты?"
        description={
          <>
            Это отменит только реквизиты для автоматического зачисления. Это не отменит уже
            отправленный перевод в сети TON. Если вы уже отправили USDT, не отменяйте реквизиты.
          </>
        }
        isLoading={isCancellingInvoice}
        cancelButton={{ children: "Не отменять", variant: "neutral" }}
        confirmButton={{
          children: "Отменить реквизиты",
          variant: "destructive",
          onClick: () => void handleCancelInvoice(),
        }}
      />
    </div>
  );
}
